import { useEffect, useState } from 'react';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { formatBRL } from '../../agenda/cliente/List/helpers';
import { useEstoqueContext } from '../Context';
import { ITEM_TYPE_LABELS, formatQuantity, isLowStock } from '../units';
import ItemForm from './ItemForm';
import { ChevronRight, PackageOpen, Plus, Search, TriangleAlert } from 'lucide-react';

const PAGE_SIZE = 50;
// A API aplica o filtro de baixo estoque depois de paginar: pede tudo para nao esconder itens.
const LOW_STOCK_LIMIT = 1000;

type StatusFilter = 'active' | 'inactive' | 'all';

export default function EstoqueItems() {
    const { items, itemsMeta, itemsLoading, itemsError, reloadItems } = useEstoqueContext();
    const { setParams } = useParamsContext();

    const [q, setQ] = useState('');
    const [type, setType] = useState('');
    const [status, setStatus] = useState<StatusFilter>('active');
    const [lowStock, setLowStock] = useState(false);
    const [limit, setLimit] = useState(PAGE_SIZE);
    const [formOpen, setFormOpen] = useState(false);

    const applyFilters = () =>
        reloadItems({
            q: q.trim() || undefined,
            type: type || undefined,
            isActive: status === 'all' ? undefined : status === 'active',
            lowStock: lowStock ? true : undefined,
            page: 1,
            limit: lowStock ? LOW_STOCK_LIMIT : limit,
        });

    useEffect(() => {
        const timer = setTimeout(applyFilters, q ? 300 : 0);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, type, status, lowStock, limit]);

    const hasFilters = !!q.trim() || !!type || status !== 'active' || lowStock;

    return (
        <div>
            <div className="sf-toolbar-row">
                <div className="sf-segmented" role="group" aria-label="Situação">
                    <button type="button" aria-pressed={status === 'active'} onClick={() => setStatus('active')}>
                        Ativos
                    </button>
                    <button type="button" aria-pressed={status === 'inactive'} onClick={() => setStatus('inactive')}>
                        Inativos
                    </button>
                    <button type="button" aria-pressed={status === 'all'} onClick={() => setStatus('all')}>
                        Todos
                    </button>
                </div>
                <label className="sf-search">
                    <Search aria-hidden />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Nome, SKU ou marca"
                        aria-label="Buscar por nome, SKU ou marca"
                    />
                </label>
                <select className="sf-select-field" value={type} onChange={(e) => setType(e.target.value)} aria-label="Tipo">
                    <option value="">Todos os tipos</option>
                    <option value="CONSUMIVEL">Consumível</option>
                    <option value="REVENDA">Revenda</option>
                </select>
                <label className="flex cursor-pointer items-center gap-2 px-1 text-sm">
                    <input
                        type="checkbox"
                        className="toggle toggle-success toggle-sm"
                        checked={lowStock}
                        onChange={(e) => setLowStock(e.target.checked)}
                    />
                    Baixo estoque
                </label>
                <button type="button" className="btn btn-primary btn-sm ml-auto" onClick={() => setFormOpen(true)}>
                    <Plus aria-hidden />
                    Novo item
                </button>
            </div>

            {itemsLoading && !items.length ? (
                <div className="space-y-2" aria-busy="true">
                    {[0, 1, 2, 3].map((item) => (
                        <div key={item} className="sf-skeleton h-16" />
                    ))}
                </div>
            ) : itemsError && !items.length ? (
                <div className="sf-state">
                    <TriangleAlert aria-hidden />
                    <h2>Não foi possível carregar os itens</h2>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => void applyFilters()}>
                        Tentar novamente
                    </button>
                </div>
            ) : items.length === 0 ? (
                <div className="sf-state">
                    {hasFilters ? <Search aria-hidden /> : <PackageOpen aria-hidden />}
                    <h2>{hasFilters ? 'Nenhum item encontrado' : 'Nenhum item cadastrado'}</h2>
                    {!hasFilters && <p>Cadastre produtos e insumos para acompanhar o saldo.</p>}
                </div>
            ) : (
                <>
                    <ul className="sf-group">
                        {items.map((row: any) => {
                            const low = row.isActive !== false && isLowStock(row);

                            return (
                                <li key={row._id}>
                                    <button
                                        type="button"
                                        className="sf-row"
                                        onClick={() => setParams({ action: 'detail', id: row._id })}
                                    >
                                        <span className="sf-row-body">
                                            <span className="sf-row-title">{row.name}</span>
                                            <span className="sf-row-sub">
                                                {[ITEM_TYPE_LABELS[row.type] || row.type, row.brand, row.sku].filter(Boolean).join(' · ')}
                                            </span>
                                            {(low || row.isActive === false) && (
                                                <span className="sf-tags">
                                                    {low && (
                                                        <span className="sf-tag" data-tone="red">
                                                            Baixo estoque
                                                        </span>
                                                    )}
                                                    {row.isActive === false && (
                                                        <span className="sf-tag" data-tone="gray">
                                                            Inativo
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                        {row.costPrice !== undefined && row.costPrice !== null && (
                                            <span className="sf-row-sub hidden w-28 shrink-0 text-right md:block">
                                                Custo {formatBRL(Number(row.costPrice) || 0)}
                                            </span>
                                        )}
                                        <span className={`sf-row-end ${low ? '!text-error' : ''}`}>
                                            {formatQuantity(row?.balance?.onHand)} {row.unit}
                                        </span>
                                        <ChevronRight className="sf-chevron" aria-hidden />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    {!lowStock && itemsMeta.total > items.length && (
                        <div className="mt-4 flex justify-center">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                disabled={itemsLoading}
                                onClick={() => setLimit((current) => current + PAGE_SIZE)}
                            >
                                Mostrar mais
                            </button>
                        </div>
                    )}
                </>
            )}

            <ItemForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={applyFilters} />
        </div>
    );
}
